/**
 * SaxoSwitchScores.jsx
 * DAS-3336: "Switches scored" list for the Saxo account view — reads DAS-3333's
 * GET /api/saxo_switch_scores and shows each switch's 1m/6m result against doing nothing
 * (owner strategy section 15).
 */
import React, { useState, useEffect } from 'react';
import api from '../functions/api';

const KIND_LABELS = {
  advice_proposed: 'advice',
  listing_change: 'same fund, other exchange',
  bond_share_change: 'bond share',
  winner_to_tracker: 'winner into tracker',
};

const kindLabel = (kind) => KIND_LABELS[kind] || 'other';

const euro0 = (v) => (v == null ? '—' : `€ ${Math.round(v).toLocaleString('nl-NL')}`);

const resultColor = (v) => {
  if (v == null) return '#999';
  return v >= 0 ? '#2e7d32' : '#c62828';
};

const SaxoSwitchScores = () => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get('/saxo_switch_scores')
      .then((res) => {
        if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => { cancelled = true; };
  }, []);

  if (rows.length === 0) {
    return (
      <div data-testid="saxo-switch-scores" style={{ marginTop: '16px' }}>
        No switches recorded
      </div>
    );
  }

  const scored = rows.filter((r) => r.result_6m_eur != null);
  const totalResult6m = scored.reduce((sum, r) => sum + r.result_6m_eur, 0);
  const waiting = rows.length - scored.length;

  return (
    <div data-testid="saxo-switch-scores" style={{ marginTop: '16px' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Switches scored</div>
      {rows.map((r, i) => (
        <div key={`${r.switch_date}-${r.sell_ticker}-${r.buy_ticker}-${i}`}
          style={{ display: 'flex', gap: '12px', padding: '4px 0', flexWrap: 'wrap' }}>
          <span>{r.switch_date}</span>
          <span>{r.sell_ticker} → {r.buy_ticker}</span>
          <span>{euro0(r.proceeds_eur)}</span>
          <span>{kindLabel(r.kind)}</span>
          {r.advice_proposed && <span>advice</span>}
          <span>
            1m:{' '}
            <span style={{ color: resultColor(r.result_1m_eur) }}>
              {r.result_1m_eur == null ? 'not yet' : euro0(r.result_1m_eur)}
            </span>
          </span>
          <span>
            6m:{' '}
            <span style={{ color: resultColor(r.result_6m_eur) }}>
              {r.result_6m_eur == null ? 'not yet' : euro0(r.result_6m_eur)}
            </span>
          </span>
        </div>
      ))}
      <div style={{ marginTop: '8px', color: '#666' }}>
        {scored.length} scored, {euro0(totalResult6m)}, {waiting} waiting
      </div>
    </div>
  );
};

export default SaxoSwitchScores;
